import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  InputSize,
  InputText,
  OnSelectChange,
  SelectSize,
  Textarea,
} from '@ui/src';
import { CRSAndFatcaFormValues } from '../../../../hoc/new-application/types';
import { CRSAndFatcaTypes } from './types';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';

import cn from '../../../corporate/aml/parts/styles.module.css';

const CRSAndFatca = ({
  setCrsAndFatcaValues,
  crsAndFatcaValues,
  crsAndFatca,
  index,
  status,
  onDeleteCRSAndFatca,
  deleteCrsAndFatcaInProgress,
  selectedCrsAndFatcaId,
  countries,
}: CRSAndFatcaTypes) => {
  const [formValues, setFormValues] = useState<CRSAndFatcaFormValues>();
  const [expanded, setExpanded] = useState(false);
  const [haveTin, setHaveTin] = useState(false);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const nextFormValues = { ...formValues };

    const nextValues = crsAndFatcaValues?.map((item) => {
      const result = { ...item };

      if (nextFormValues.id === item.id) {
        if (e.target.name === `residenceCountryId-${item.id}`) {
          nextFormValues.residenceCountryId = e.target.value as string;
          result.residenceCountryId = e.target.value as string;
        } else if (e.target.name === `taxIdentificationNumber-${item.id}`) {
          nextFormValues.taxIdentificationNumber = e.target.value as string;
          result.taxIdentificationNumber = e.target.value as string;
        } else if (e.target.name === `noTinReason-${item.id}`) {
          nextFormValues.noTinReason = e.target.value as string;
          result.noTinReason = e.target.value as string;
        }
      }

      return result;
    });

    setFormValues(nextFormValues as CRSAndFatcaFormValues);
    setCrsAndFatcaValues(nextValues as CRSAndFatcaFormValues[]);
  };

  const onChangeHaveTin = (checked: boolean) => {
    setHaveTin(checked);

    const nextFormValues = { ...formValues };

    const nextValues = crsAndFatcaValues?.map((item) => {
      const result = { ...item };

      if (nextFormValues.id === item.id) {
        if (checked) {
          nextFormValues.noTinReason = '';
          result.noTinReason = '';
        } else {
          nextFormValues.taxIdentificationNumber = '';
          result.taxIdentificationNumber = '';
        }
      }

      return result;
    });

    setFormValues(nextFormValues as CRSAndFatcaFormValues);
    setCrsAndFatcaValues(nextValues as CRSAndFatcaFormValues[]);
  };

  useEffect(() => {
    if (crsAndFatca) {
      setFormValues(crsAndFatca);

      if (crsAndFatca?.id?.includes('crs')) {
        setExpanded(true);
      }
    }
  }, [crsAndFatca]);

  useEffect(() => {
    if (formValues?.taxIdentificationNumber) {
      setHaveTin(true);
    }
  }, [formValues]);

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {`Tax residence #${index + 1}`}
          </button>

          {status !== CustomerStatus.CLOSED && (
            <button
              type="button"
              className={cn.deleteAccountCurrencyButton}
              onClick={() => onDeleteCRSAndFatca(crsAndFatca?.id as string)}
            >
              {deleteCrsAndFatcaInProgress &&
              selectedCrsAndFatcaId === crsAndFatca?.id ? (
                <IconLoader width={18} height={18} />
              ) : (
                <IconTrash />
              )}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className={cn.accountCurrencyContent}>
          <RegulatedCompanyRow title="Country" annualIncome accountsCurrencies>
            <Autocomplete
              name={`residenceCountryId-${crsAndFatca?.id}`}
              placeholder="Select currency..."
              list={countries}
              value={formValues?.residenceCountryId}
              size={SelectSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `residenceCountryId-${crsAndFatca?.id}`,
                    value: e.target.value as string,
                  },
                })
              }
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              borderless
              fullWidth
            />
          </RegulatedCompanyRow>
          <div className={cn.tinCheckboxWrapper}>
            <Checkbox
              name={`haveTin-${crsAndFatca?.id}`}
              type="checkbox"
              checked={haveTin}
              onChange={(e) => onChangeHaveTin(e.target.checked)}
              title="Having a TIN"
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              labelClassName={cn.addressCheckbox}
            />
          </div>
          {haveTin && (
            <RegulatedCompanyRow title="TIN" annualIncome accountsCurrencies>
              <InputText
                name={`taxIdentificationNumber-${crsAndFatca?.id}`}
                placeholder="Select TIN..."
                value={formValues?.taxIdentificationNumber}
                size={InputSize.SMALL}
                onChange={(e) => {
                  onChange({
                    target: {
                      name: `taxIdentificationNumber-${crsAndFatca?.id}`,
                      value: e.target.value,
                    },
                  });
                }}
                disabled={status === CustomerStatus.CLOSED}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                className="borderless"
              />
            </RegulatedCompanyRow>
          )}
          {!haveTin && (
            <RegulatedCompanyRow
              title="Reason for no TIN"
              annualIncome
              accountsCurrencies
              textarea
            >
              <Textarea
                name={`noTinReason-${crsAndFatca?.id}`}
                placeholder="Enter description..."
                value={formValues?.noTinReason}
                onChange={(e) => {
                  onChange({
                    target: {
                      name: `noTinReason-${crsAndFatca?.id}`,
                      value: e.target.value,
                    },
                  });
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                disabled={status === CustomerStatus.CLOSED}
              />
            </RegulatedCompanyRow>
          )}
        </div>
      )}
    </div>
  );
};

export default CRSAndFatca;
